import React, { useState, useEffect, useMemo } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonBackButton,
  IonButtons,
  IonButton,
  IonIcon,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
  useIonRouter,
  useIonToast,
} from '@ionic/react';
import {
  call,
  personAdd,
  download,
  people,
  layers,
  musicalNotes,
  school,
  shield,
  logoWhatsapp,
  close,
  trashOutline,
  checkmarkOutline,
  swapHorizontalOutline,
  calendarOutline,
  searchOutline,
  chevronForwardOutline,
  listOutline,
  arrowUpOutline,
  arrowDownOutline,
  create as createIcon,
  addOutline,
} from 'ionicons/icons';
import { useParams } from 'react-router-dom';
import {
  useGroup,
  useGroupMembers,
  useRemoveGroupMember,
  useUpdateGroupMemberRole,
  useAddGroupMembersBatch,
  useUpdateGroupMemberSection,
  useCreateGroupSection,
  useUpdateGroupSection,
  useDeleteGroupSection,
  useReorderGroupSections,
} from '@/hooks/useGroups';
import { exportGroupMembers } from '@/api/groups';
import { useMembersList } from '@/hooks/useMembers';
import { usePermission } from '@/hooks/usePermission';
import { GroupCategory, type GroupMemberRole } from '@/types/group';

/** Role cycle & display: Dirijor (conductor) is only available for muzical
 *  groups; everywhere else it falls back to the two-role cycle. */
function nextRoleInCycle(current: string | null, isMuzical: boolean): GroupMemberRole {
  const currentNorm = current ?? 'member';
  if (isMuzical) {
    if (currentNorm === 'member') return 'leader';
    if (currentNorm === 'leader') return 'dirijor';
    return 'member';
  }
  return currentNorm === 'leader' ? 'member' : 'leader';
}

function roleLabel(role: string | null | undefined): string {
  if (role === 'leader') return 'Lider';
  if (role === 'dirijor') return 'Dirijor';
  return 'Membru';
}
import { GROUP_CATEGORY_LABELS, GROUP_CATEGORY_COLORS } from '@/utils/labels';

function sectionLabels(groupName: string | undefined) {
  const n = (groupName ?? '').toLowerCase();
  if (n.includes('cor '))
    return {
      pl: 'Voci', S: 'Voce', none: 'Neatribuit',
      change: 'Schimbă vocea', newPh: 'Voce nouă…',
      added: 'Voce adăugată', renamed: 'Voce redenumită',
      deleted: 'Voce ștearsă', updated: 'Voce actualizată',
      existsErr: 'Există deja o voce cu acest nume.',
      delTitle: 'Șterge vocea',
      delMsg: 'Membrii rămân în grup, însă își pierd atribuirea la această voce. Ești sigur?',
      desc: 'Organizează membrii pe voci. Atribuirea se face din cardul fiecărui membru.',
    };
  if (n.includes('fanfar') || n.includes('orchestr'))
    return {
      pl: 'Instrumente', S: 'Instrument', none: 'Neatribuit',
      change: 'Schimbă instrumentul', newPh: 'Instrument nou…',
      added: 'Instrument adăugat', renamed: 'Instrument redenumit',
      deleted: 'Instrument șters', updated: 'Instrument actualizat',
      existsErr: 'Există deja un instrument cu acest nume.',
      delTitle: 'Șterge instrumentul',
      delMsg: 'Membrii rămân în grup, însă își pierd atribuirea la acest instrument. Ești sigur?',
      desc: 'Organizează membrii pe instrumente. Atribuirea se face din cardul fiecărui membru.',
    };
  return {
    pl: 'Secțiuni', S: 'Secțiune', none: 'Neatribuit',
    change: 'Schimbă secțiunea', newPh: 'Secțiune nouă…',
    added: 'Secțiune adăugată', renamed: 'Secțiune redenumită',
    deleted: 'Secțiune ștearsă', updated: 'Secțiune actualizată',
    existsErr: 'Există deja o secțiune cu acest nume.',
    delTitle: 'Șterge secțiunea',
    delMsg: 'Membrii rămân în grup, însă își pierd atribuirea la această secțiune. Ești sigur?',
    desc: 'Organizează membrii pe secțiuni. Atribuirea se face din cardul fiecărui membru.',
  };
}
import AvatarGroup from '@/components/shared/AvatarGroup';
import GroupAttendancePulse from '@/components/shared/GroupAttendancePulse';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import GroupAttendanceTab from './GroupAttendanceTab';

const CATEGORY_LABELS = GROUP_CATEGORY_LABELS;

const CATEGORY_ICONS: Record<string, string> = {
  [GroupCategory.SECTOR]: layers,
  [GroupCategory.TINERI]: people,
  [GroupCategory.MUZICAL]: musicalNotes,
  [GroupCategory.SBC]: school,
  [GroupCategory.ALTELE]: shield,
};

const CATEGORY_COLORS = GROUP_CATEGORY_COLORS;

type TabKey = 'members' | 'attendance' | 'sections' | 'export';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [breakpoint]);
  return isMobile;
}

const GroupDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const router = useIonRouter();
  const { data: group, isLoading: groupLoading } = useGroup(id);
  const { data: members, isLoading: membersLoading, refetch } = useGroupMembers(id);
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>('members');
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [addRole, setAddRole] = useState<GroupMemberRole>('member');
  const [addSectionId, setAddSectionId] = useState<string | null>(null);
  const [removeMemberId, setRemoveMemberId] = useState<string | null>(null);
  const { can } = usePermission();
  const isAdmin = can('groups.manage_members');
  const canChangeRole = can('groups.change_member_role');
  const canManageSections = can('groups.manage_sections');
  const [presentToast] = useIonToast();
  const [exporting, setExporting] = useState(false);
  const isMobile = useIsMobile();

  // Batch add state
  const [selectedForAdd, setSelectedForAdd] = useState<Set<string>>(new Set());
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedMemberSearch(memberSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [memberSearch]);

  const removeMemberMut = useRemoveGroupMember();
  const updateRoleMut = useUpdateGroupMemberRole();
  const batchAddMut = useAddGroupMembersBatch();
  const updateSectionMut = useUpdateGroupMemberSection();
  const createSectionMut = useCreateGroupSection();
  const updateSectionNameMut = useUpdateGroupSection();
  const deleteSectionMut = useDeleteGroupSection();
  const reorderSectionsMut = useReorderGroupSections();

  const [newSectionName, setNewSectionName] = useState('');
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingSectionName, setEditingSectionName] = useState('');
  const [deletingSectionId, setDeletingSectionId] = useState<string | null>(null);

  const [sectionEditMember, setSectionEditMember] = useState<{ memberId: string; memberName: string; currentSectionId: string | null } | null>(null);
  const sectionMap = useMemo(() => {
    const m = new Map<string, string>();
    (group?.sections ?? []).forEach((s) => m.set(s.id, s.name));
    return m;
  }, [group?.sections]);
  const { data: allMembers, isFetching: isSearchingMembers } = useMembersList({
    search: debouncedMemberSearch || undefined,
    page_size: 30,
  });

  const handleRefresh = async (event: CustomEvent) => {
    await refetch();
    event.detail.complete();
  };

  const filteredMembers = useMemo(() => {
    if (!members) return [];
    if (!searchText) return members;
    const query = searchText.toLowerCase();
    return members.filter(
      (m) => m.member_name?.toLowerCase().includes(query),
    );
  }, [members, searchText]);

  const isMuzicalGroup = group?.category === GroupCategory.MUZICAL;
  const sl = sectionLabels(group?.name);

  const leaders = useMemo(() => {
    if (!members) return [];
    return members.filter((m) => m.role === 'leader' || m.role === 'dirijor');
  }, [members]);

  const catColor = (group?.category ? CATEGORY_COLORS[group.category] : undefined) ?? '#64748b';
  const catIcon = (group?.category ? CATEGORY_ICONS[group.category] : undefined) ?? shield;

  // ── Sections admin (create / rename / reorder / delete) ──
  const handleCreateSection = async () => {
    if (!id) return;
    const name = newSectionName.trim();
    if (!name) return;
    try {
      await createSectionMut.mutateAsync({ groupId: id, name });
      setNewSectionName('');
      void presentToast({ message: `${sl.added}: ${name}`, duration: 1600, color: 'success', position: 'bottom' });
    } catch (e: any) {
      const msg = e?.response?.status === 422
        ? sl.existsErr
        : 'Eroare la creare.';
      void presentToast({ message: msg, duration: 2200, color: 'danger', position: 'bottom' });
    }
  };

  const startRenameSection = (sectionId: string, currentName: string) => {
    setEditingSectionId(sectionId);
    setEditingSectionName(currentName);
  };
  const cancelRenameSection = () => {
    setEditingSectionId(null);
    setEditingSectionName('');
  };
  const commitRenameSection = async () => {
    if (!id || !editingSectionId) return;
    const name = editingSectionName.trim();
    const original = group?.sections?.find((s) => s.id === editingSectionId);
    if (!name || name === original?.name) {
      cancelRenameSection();
      return;
    }
    try {
      await updateSectionNameMut.mutateAsync({ sectionId: editingSectionId, groupId: id, name });
      void presentToast({ message: `${sl.renamed}.`, duration: 1400, color: 'success', position: 'bottom' });
    } catch (e: any) {
      const msg = e?.response?.status === 422
        ? sl.existsErr
        : 'Eroare la redenumire.';
      void presentToast({ message: msg, duration: 2200, color: 'danger', position: 'bottom' });
    } finally {
      cancelRenameSection();
    }
  };

  const moveSection = async (sectionId: string, direction: -1 | 1) => {
    if (!id || !group?.sections) return;
    const ordered = [...group.sections].sort((a, b) => a.sort_order - b.sort_order);
    const idx = ordered.findIndex((s) => s.id === sectionId);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= ordered.length) return;
    const copy = [...ordered];
    const [moved] = copy.splice(idx, 1);
    copy.splice(target, 0, moved!);
    try {
      await reorderSectionsMut.mutateAsync({ groupId: id, sectionIds: copy.map((s) => s.id) });
    } catch {
      void presentToast({ message: 'Eroare la reordonare.', duration: 2000, color: 'danger', position: 'bottom' });
    }
  };

  const handleDeleteSection = async () => {
    if (!id || !deletingSectionId) return;
    try {
      await deleteSectionMut.mutateAsync({ sectionId: deletingSectionId, groupId: id });
      void presentToast({ message: `${sl.deleted}.`, duration: 1500, color: 'success', position: 'bottom' });
    } catch {
      void presentToast({ message: 'Eroare la ștergere.', duration: 2000, color: 'danger', position: 'bottom' });
    } finally {
      setDeletingSectionId(null);
    }
  };

  const handleSectionChange = async (memberId: string, sectionId: string | null) => {
    if (!id) return;
    try {
      await updateSectionMut.mutateAsync({ groupId: id, memberId, sectionId });
      void presentToast({ message: `${sl.updated}.`, duration: 1500, color: 'success', position: 'bottom' });
    } catch {
      void presentToast({ message: 'Eroare la schimbare.', duration: 2000, color: 'danger', position: 'bottom' });
    } finally {
      setSectionEditMember(null);
    }
  };

  // ── Role toggle ──
  const handleRoleToggle = async (memberId: string, currentRole: string | null) => {
    if (!id) return;
    const newRole = nextRoleInCycle(currentRole, isMuzicalGroup);
    try {
      await updateRoleMut.mutateAsync({ groupId: id, memberId, role: newRole });
      void presentToast({ message: `Rol schimbat în ${roleLabel(newRole)}.`, duration: 1500, color: 'success', position: 'bottom' });
    } catch {
      void presentToast({ message: 'Eroare la schimbarea rolului.', duration: 2000, color: 'danger', position: 'bottom' });
    }
  };

  // ── Export ──
  const handleExport = async () => {
    if (!id) return;
    setExporting(true);
    try {
      const blob = await exportGroupMembers(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `grup_${group?.name ?? 'export'}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      void presentToast({ message: 'Export descărcat.', duration: 2000, color: 'success', position: 'bottom' });
    } catch {
      void presentToast({ message: 'Eroare la export.', duration: 2000, color: 'danger', position: 'bottom' });
    } finally {
      setExporting(false);
    }
  };

  // ── Batch add ──
  const toggleSelectForAdd = (memberId: string) => {
    setSelectedForAdd((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId); else next.add(memberId);
      return next;
    });
  };

  const handleBatchAdd = async () => {
    if (!id || selectedForAdd.size === 0) return;
    try {
      await batchAddMut.mutateAsync({
        groupId: id,
        members: [...selectedForAdd].map((mid) => ({
          member_id: mid,
          role: addRole,
          section_id: addSectionId,
        })),
      });
      void presentToast({ message: `${selectedForAdd.size} membri adăugați.`, duration: 2000, color: 'success', position: 'bottom' });
      setSelectedForAdd(new Set());
      setMemberSearch('');
      setAddSectionId(null);
    } catch {
      void presentToast({ message: 'Eroare la adăugare.', duration: 2000, color: 'danger', position: 'bottom' });
    }
  };

  if (groupLoading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar className="crm-toolbar">
            <IonButtons slot="start"><IonBackButton defaultHref="/groups" /></IonButtons>
            <IonTitle><IonSkeletonText animated style={{ width: '50%' }} /></IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonSkeletonText animated style={{ width: '100%', height: 100 }} />
        </IonContent>
      </IonPage>
    );
  }

  const getInitial = (name: string | null | undefined) => (name?.[0] ?? 'G').toUpperCase();

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="crm-toolbar">
          <IonButtons slot="start">
            <IonBackButton defaultHref="/groups" />
          </IonButtons>
          <IonTitle>Detalii Grup</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className="crm-page-layout">
          {/* === LEFT SIDEBAR === */}
          <div className="crm-stats-panel">
            <div className="crm-detail-sidebar">
              <div className="crm-detail-sidebar__header crm-animate-in">
                <div className="crm-detail-sidebar__icon" style={{ backgroundColor: catColor }}>
                  <IonIcon icon={catIcon} />
                </div>
                <div>
                  <h3 className="crm-detail-sidebar__name">{group?.name ?? 'Grup'}</h3>
                  <p className="crm-detail-sidebar__category">
                    {(group?.category ? CATEGORY_LABELS[group.category] : undefined) ?? 'Altele'}
                  </p>
                </div>
              </div>

              {group?.description && (
                <div className="crm-animate-in">
                  <h4 className="crm-detail-sidebar__section-title">Descriere</h4>
                  <p className="crm-detail-sidebar__text">{group.description}</p>
                </div>
              )}

              <div className="crm-detail-sidebar__meta crm-animate-in">
                <div className="crm-detail-sidebar__meta-chip">
                  <IonIcon icon={people} style={{ fontSize: 14 }} />
                  {members?.length ?? group?.member_count ?? 0} Membri
                </div>
              </div>

              {leaders.length > 0 && (
                <div className="crm-animate-in">
                  <h4 className="crm-detail-sidebar__section-title">Lideri</h4>
                  <AvatarGroup
                    items={leaders.map((m) => ({
                      name: m.member_name,
                      photo_url: m.member_photo_url,
                    }))}
                    max={6}
                    size={34}
                  />
                  <div style={{ marginTop: 6 }}>
                    {leaders.map((m) => (
                      <p key={m.id} style={{ fontSize: 12, color: '#475569', margin: '2px 0' }}>
                        {m.member_name}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {members && members.length > 0 && (
                <div className="crm-animate-in">
                  <h4 className="crm-detail-sidebar__section-title">Membri Grup</h4>
                  <AvatarGroup
                    items={members.map((m) => ({
                      name: m.member_name,
                      photo_url: m.member_photo_url,
                    }))}
                    max={8}
                    size={30}
                  />
                </div>
              )}

              {members && (
                <div className="crm-detail-sidebar__progress crm-animate-in">
                  <div className="crm-detail-sidebar__progress-header">
                    <span className="crm-detail-sidebar__progress-label">Total Membri</span>
                    <span className="crm-detail-sidebar__progress-value">{members.length}</span>
                  </div>
                  <div className="crm-detail-sidebar__progress-bar">
                    <div
                      className="crm-detail-sidebar__progress-fill"
                      style={{ width: '100%', backgroundColor: catColor }}
                    />
                  </div>
                </div>
              )}

              <div className="crm-animate-in">
                <GroupAttendancePulse groupId={id} memberCount={members?.length ?? 0} />
              </div>
            </div>
          </div>

          {/* === MAIN CONTENT === */}
          <div className="crm-main-content">
            <div className="crm-tabs-row">
              <div className="crm-tabs" style={{ marginBottom: 0 }}>
                <button
                  className={`crm-tab ${activeTab === 'members' ? 'crm-tab--active' : ''}`}
                  onClick={() => setActiveTab('members')}
                >
                  <IonIcon icon={people} style={{ fontSize: 14 }} /> Membri
                </button>
                <button
                  className={`crm-tab ${activeTab === 'attendance' ? 'crm-tab--active' : ''}`}
                  onClick={() => setActiveTab('attendance')}
                >
                  <IonIcon icon={calendarOutline} style={{ fontSize: 14 }} /> Prezență
                </button>
                {canManageSections && (
                  <button
                    className={`crm-tab ${activeTab === 'sections' ? 'crm-tab--active' : ''}`}
                    onClick={() => setActiveTab('sections')}
                  >
                    <IonIcon icon={listOutline} style={{ fontSize: 14 }} /> {sl.pl}
                  </button>
                )}
                <button
                  className={`crm-tab ${activeTab === 'export' ? 'crm-tab--active' : ''}`}
                  onClick={() => setActiveTab('export')}
                >
                  <IonIcon icon={download} style={{ fontSize: 14 }} /> Export
                </button>
              </div>

              {isAdmin && activeTab === 'members' && (
                <div className="crm-tabs-row__action">
                  <IonButton fill="outline" size="small" style={{ '--border-radius': '8px' }} onClick={() => { setShowAddMember(true); setSelectedForAdd(new Set()); setAddSectionId(null); }}>
                    <IonIcon icon={personAdd} slot="start" />
                    Adaugă Membru
                  </IonButton>
                </div>
              )}
            </div>

            {activeTab === 'members' && (
              <>
                <div className="crm-search-pill" style={{ marginBottom: 12 }}>
                  <IonIcon icon={searchOutline} className="crm-search-pill__icon" />
                  <input
                    type="text"
                    className="crm-search-pill__input"
                    placeholder="Caută membri…"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  {searchText && (
                    <button className="crm-search-pill__clear" onClick={() => setSearchText('')} aria-label="Șterge">
                      <IonIcon icon={close} />
                    </button>
                  )}
                </div>

                {isMobile ? (
                  /* ── Mobile: card list ── */
                  membersLoading ? (
                    <div className="crm-member-cards">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="crm-member-card" style={{ cursor: 'default' }}>
                          <IonSkeletonText animated style={{ width: 44, height: 44, borderRadius: '50%' }} />
                          <div style={{ flex: 1 }}>
                            <IonSkeletonText animated style={{ width: '60%', height: 14 }} />
                            <IonSkeletonText animated style={{ width: '40%', height: 11, marginTop: 6 }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="crm-member-cards">
                      {filteredMembers.map((member) => (
                        <div
                          key={member.id}
                          className="crm-member-card"
                          onClick={() => router.push(`/members/${member.member_id}`)}
                        >
                          <div
                            className="crm-member-card__avatar"
                            style={{ backgroundColor: catColor }}
                          >
                            {member.member_photo_url ? (
                              <img src={member.member_photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              getInitial(member.member_name)
                            )}
                          </div>
                          <div className="crm-member-card__body">
                            <div className="crm-member-card__name">{member.member_name}</div>
                            <div className="crm-member-card__meta">
                              {(member.role === 'leader' || member.role === 'dirijor') && (
                                <span className="crm-member-card__role">{roleLabel(member.role)}</span>
                              )}
                              {(group?.sections?.length ?? 0) > 0 && (
                                isAdmin ? (
                                  <button
                                    type="button"
                                    className="crm-section-badge crm-section-badge--interactive"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSectionEditMember({
                                        memberId: member.member_id,
                                        memberName: member.member_name ?? '',
                                        currentSectionId: member.section_id,
                                      });
                                    }}
                                    style={member.section_id ? {
                                      color: catColor,
                                      borderColor: `${catColor}40`,
                                      backgroundColor: `${catColor}14`,
                                    } : undefined}
                                  >
                                    {member.section_id ? sectionMap.get(member.section_id) ?? '—' : sl.none}
                                  </button>
                                ) : member.section_id ? (
                                  <span
                                    className="crm-section-badge"
                                    style={{
                                      color: catColor,
                                      borderColor: `${catColor}40`,
                                      backgroundColor: `${catColor}14`,
                                    }}
                                  >
                                    {sectionMap.get(member.section_id) ?? '—'}
                                  </span>
                                ) : null
                              )}
                              {member.member_phone && <span>{member.member_phone}</span>}
                              {!member.member_phone && !member.role && !member.section_id && <span>—</span>}
                            </div>
                          </div>
                          <div className="crm-member-card__actions">
                            {member.member_phone && (
                              <>
                                <a
                                  href={`tel:${member.member_phone}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="crm-iconbtn crm-iconbtn--call"
                                  aria-label="Sună"
                                >
                                  <IonIcon icon={call} style={{ fontSize: 16 }} />
                                </a>
                                <a
                                  href={`https://wa.me/${((d) => d.startsWith('0') ? '40' + d.slice(1) : d)(member.member_phone.replace(/\D/g, ''))}`}
                                  onClick={(e) => e.stopPropagation()}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="crm-iconbtn crm-iconbtn--whatsapp"
                                  aria-label="WhatsApp"
                                >
                                  <IonIcon icon={logoWhatsapp} style={{ fontSize: 16 }} />
                                </a>
                              </>
                            )}
                            {!member.member_phone && (
                              <IonIcon icon={chevronForwardOutline} style={{ fontSize: 18, color: '#cbd5e1' }} />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                <div className="crm-card" style={{ overflow: 'auto' }}>
                  {membersLoading ? (
                    <div style={{ padding: 20 }}>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                          <IonSkeletonText animated style={{ width: '60%', height: 16 }} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <table className="crm-table">
                      <thead>
                        <tr>
                          <th style={{ minWidth: 200 }}>Nume</th>
                          <th style={{ width: 130 }}>Telefon</th>
                          <th style={{ width: 100 }}>Rol</th>
                          {(group?.sections?.length ?? 0) > 0 && (
                            <th style={{ width: 130 }}>{sl.S}</th>
                          )}
                          <th style={{ width: 80 }}>Acțiuni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMembers.map((member) => (
                          <tr
                            key={member.id}
                            onClick={() => router.push(`/members/${member.member_id}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div
                                  style={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: '50%',
                                    backgroundColor: catColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    flexShrink: 0,
                                    overflow: 'hidden',
                                  }}
                                >
                                  {member.member_photo_url ? (
                                    <img
                                      src={member.member_photo_url}
                                      alt=""
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  ) : (
                                    getInitial(member.member_name)
                                  )}
                                </div>
                                <div className="crm-table__name">{member.member_name}</div>
                              </div>
                            </td>
                            <td>
                              {member.member_phone ? (
                                <span style={{ fontSize: 13, color: '#475569' }}>{member.member_phone}</span>
                              ) : (
                                <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>
                              )}
                            </td>
                            <td>
                              {canChangeRole ? (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleRoleToggle(member.member_id, member.role); }}
                                  title={isMuzicalGroup ? 'Click: Membru → Lider → Dirijor' : 'Click pentru a schimba rolul'}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                    border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                                    color: member.role === 'dirijor' ? '#9333ea'
                                      : member.role === 'leader' ? '#4f46e5'
                                        : '#94a3b8',
                                    background: member.role === 'dirijor' ? '#faf5ff'
                                      : member.role === 'leader' ? '#eef2ff'
                                        : '#f8fafc',
                                  }}
                                >
                                  {roleLabel(member.role)}
                                  <IonIcon icon={swapHorizontalOutline} style={{ fontSize: 11 }} />
                                </button>
                              ) : (
                                member.role === 'dirijor' ? (
                                  <span
                                    className="crm-badge"
                                    style={{ background: '#faf5ff', color: '#9333ea' }}
                                  >Dirijor</span>
                                ) : member.role === 'leader' ? (
                                  <span className="crm-badge crm-badge--leader">Lider</span>
                                ) : (
                                  <span style={{ fontSize: 12, color: '#94a3b8' }}>Membru</span>
                                )
                              )}
                            </td>
                            {(group?.sections?.length ?? 0) > 0 && (
                              <td>
                                {isAdmin ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSectionEditMember({
                                        memberId: member.member_id,
                                        memberName: member.member_name ?? '',
                                        currentSectionId: member.section_id,
                                      });
                                    }}
                                    className="crm-section-badge crm-section-badge--interactive"
                                    style={member.section_id ? {
                                      color: catColor,
                                      borderColor: `${catColor}40`,
                                      backgroundColor: `${catColor}14`,
                                    } : undefined}
                                  >
                                    {member.section_id ? sectionMap.get(member.section_id) ?? '—' : sl.none}
                                  </button>
                                ) : member.section_id ? (
                                  <span
                                    className="crm-section-badge"
                                    style={{
                                      color: catColor,
                                      borderColor: `${catColor}40`,
                                      backgroundColor: `${catColor}14`,
                                    }}
                                  >
                                    {sectionMap.get(member.section_id) ?? '—'}
                                  </span>
                                ) : (
                                  <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>
                                )}
                              </td>
                            )}
                            <td>
                              <div className="crm-table__actions">
                                {member.member_phone && (
                                  <>
                                    <a
                                      href={`tel:${member.member_phone}`}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        width: 30, height: 30, borderRadius: 8,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        color: '#4f46e5', background: '#eef2ff', textDecoration: 'none',
                                      }}
                                    >
                                      <IonIcon icon={call} style={{ fontSize: 14 }} />
                                    </a>
                                    <a
                                      href={`https://wa.me/${((d) => d.startsWith('0') ? '40' + d.slice(1) : d)(member.member_phone.replace(/\D/g, ''))}`}
                                      onClick={(e) => e.stopPropagation()}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        width: 30, height: 30, borderRadius: 8,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        color: '#059669', background: '#ecfdf5', textDecoration: 'none',
                                      }}
                                    >
                                      <IonIcon icon={logoWhatsapp} style={{ fontSize: 14 }} />
                                    </a>
                                  </>
                                )}
                                {isAdmin && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setRemoveMemberId(member.member_id); }}
                                    style={{
                                      width: 30, height: 30, borderRadius: 8,
                                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                      color: '#dc2626', background: '#fef2f2', border: 'none', cursor: 'pointer',
                                    }}
                                  >
                                    <IonIcon icon={trashOutline} style={{ fontSize: 14 }} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {!membersLoading && filteredMembers.length === 0 && (
                    <div className="crm-empty-state">
                      <div className="crm-empty-state__icon"><IonIcon icon={people} /></div>
                      <h3 className="crm-empty-state__title">
                        {searchText ? 'Niciun rezultat' : 'Niciun membru'}
                      </h3>
                      <p className="crm-empty-state__text">
                        {searchText
                          ? 'Niciun membru nu corespunde căutării.'
                          : 'Acest grup nu are încă membri.'}
                      </p>
                    </div>
                  )}
                </div>
                )}

                {/* Shared empty state for mobile card layout */}
                {isMobile && !membersLoading && filteredMembers.length === 0 && (
                  <div className="crm-empty-state">
                    <div className="crm-empty-state__icon"><IonIcon icon={people} /></div>
                    <h3 className="crm-empty-state__title">
                      {searchText ? 'Niciun rezultat' : 'Niciun membru'}
                    </h3>
                    <p className="crm-empty-state__text">
                      {searchText
                        ? 'Niciun membru nu corespunde căutării.'
                        : 'Acest grup nu are încă membri.'}
                    </p>
                  </div>
                )}
              </>
            )}

            {activeTab === 'attendance' && id && group && (
              <GroupAttendanceTab
                groupId={id}
                meetingDay={group.meeting_day ?? null}
                attendanceMode={group.attendance_mode ?? 'roster'}
                canRecord={can('attendance.record')}
                canDelete={can('attendance.delete')}
              />
            )}

            {activeTab === 'sections' && canManageSections && group && (
              <div className="crm-card" style={{ padding: 20, maxWidth: 560 }}>
                <div style={{ marginBottom: 14 }}>
                  <h3 className="crm-section-title">{sl.pl}</h3>
                  <p className="crm-section-subtitle" style={{ marginTop: 2 }}>
                    {sl.desc}
                  </p>
                </div>

                {(() => {
                  const ordered = [...(group.sections ?? [])].sort((a, b) => a.sort_order - b.sort_order);
                  if (ordered.length === 0) {
                    return (
                      <div className="crm-empty-state" style={{ padding: '24px 16px', marginBottom: 14 }}>
                        <p className="crm-empty-state__text" style={{ margin: 0 }}>
                          Încă nimic. Adaugă mai jos.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <ul className="crm-section-list">
                      {ordered.map((s, idx) => {
                        const isEditing = editingSectionId === s.id;
                        const memberCount = members?.filter((m) => m.section_id === s.id).length ?? 0;
                        return (
                          <li key={s.id} className="crm-section-list__row">
                            <div className="crm-section-list__drag" aria-hidden="true">
                              <button
                                type="button"
                                className="crm-section-list__order-btn"
                                onClick={() => moveSection(s.id, -1)}
                                disabled={idx === 0 || reorderSectionsMut.isPending}
                                aria-label="Mută mai sus"
                              >
                                <IonIcon icon={arrowUpOutline} />
                              </button>
                              <button
                                type="button"
                                className="crm-section-list__order-btn"
                                onClick={() => moveSection(s.id, 1)}
                                disabled={idx === ordered.length - 1 || reorderSectionsMut.isPending}
                                aria-label="Mută mai jos"
                              >
                                <IonIcon icon={arrowDownOutline} />
                              </button>
                            </div>
                            <div className="crm-section-list__body">
                              {isEditing ? (
                                <input
                                  autoFocus
                                  className="crm-section-list__input"
                                  value={editingSectionName}
                                  onChange={(e) => setEditingSectionName(e.target.value)}
                                  onBlur={commitRenameSection}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                    if (e.key === 'Escape') cancelRenameSection();
                                  }}
                                  maxLength={60}
                                />
                              ) : (
                                <button
                                  type="button"
                                  className="crm-section-list__name"
                                  onClick={() => startRenameSection(s.id, s.name)}
                                  title="Click pentru a redenumi"
                                >
                                  {s.name}
                                </button>
                              )}
                              <span className="crm-section-list__count">
                                {memberCount} {memberCount === 1 ? 'membru' : 'membri'}
                              </span>
                            </div>
                            <div className="crm-section-list__actions">
                              {!isEditing && (
                                <button
                                  type="button"
                                  className="crm-section-list__icon-btn"
                                  onClick={() => startRenameSection(s.id, s.name)}
                                  aria-label="Redenumește"
                                >
                                  <IonIcon icon={createIcon} />
                                </button>
                              )}
                              <button
                                type="button"
                                className="crm-section-list__icon-btn crm-section-list__icon-btn--danger"
                                onClick={() => setDeletingSectionId(s.id)}
                                disabled={deleteSectionMut.isPending}
                                aria-label="Șterge"
                              >
                                <IonIcon icon={trashOutline} />
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  );
                })()}

                <div className="crm-section-list__add">
                  <input
                    type="text"
                    className="crm-section-list__input"
                    placeholder={sl.newPh}
                    value={newSectionName}
                    onChange={(e) => setNewSectionName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newSectionName.trim()) handleCreateSection();
                    }}
                    maxLength={60}
                  />
                  <button
                    type="button"
                    className="form-btn form-btn--primary"
                    onClick={handleCreateSection}
                    disabled={!newSectionName.trim() || createSectionMut.isPending}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    {createSectionMut.isPending
                      ? <span className="login-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                      : <IonIcon icon={addOutline} style={{ fontSize: 16 }} />}
                    Adaugă
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'export' && (
              <div className="crm-card" style={{ padding: 40, textAlign: 'center' }}>
                <IonIcon icon={download} style={{ fontSize: 40, color: '#94a3b8', marginBottom: 16 }} />
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>
                  Exportă Lista Membrilor
                </h3>
                <p style={{ fontSize: 13, color: '#94a3b8', margin: '0 0 20px' }}>
                  Descarcă lista completă a membrilor din acest grup în format CSV.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button
                    className="form-btn form-btn--primary"
                    onClick={handleExport}
                    disabled={exporting || !members?.length}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    {exporting && <span className="login-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />}
                    <IonIcon icon={download} style={{ fontSize: 16 }} />
                    Export CSV
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Add Member Modal (Batch) ── */}
        {showAddMember && (
          <div className="logout-overlay" onClick={() => setShowAddMember(false)}>
            <div className="logout-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, width: '90%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Adaugă Membri în Grup</h3>
                <button onClick={() => setShowAddMember(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                  <IonIcon icon={close} style={{ fontSize: 22, color: '#94a3b8' }} />
                </button>
              </div>

              <input
                type="text"
                placeholder="Caută persoană..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                autoFocus
                style={{
                  width: '100%', padding: '10px 0', fontSize: 14, fontWeight: 700,
                  fontFamily: "'Lato', sans-serif", color: '#1c1d21',
                  border: 'none', borderBottom: '1px solid #ececf2', outline: 'none',
                  background: 'transparent', marginBottom: 8,
                }}
                onFocus={(e) => { (e.target.style.borderBottomColor = '#5e81f4'); }}
                onBlur={(e) => { (e.target.style.borderBottomColor = '#ececf2'); }}
              />

              {canChangeRole && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setAddRole('member')}
                    className={`form-chip${addRole === 'member' ? ' form-chip--active' : ''}`}
                  >
                    Membru
                  </button>
                  <button
                    onClick={() => setAddRole('leader')}
                    className={`form-chip${addRole === 'leader' ? ' form-chip--active' : ''}`}
                  >
                    Lider
                  </button>
                  {isMuzicalGroup && (
                    <button
                      onClick={() => setAddRole('dirijor')}
                      className={`form-chip${addRole === 'dirijor' ? ' form-chip--active' : ''}`}
                    >
                      Dirijor
                    </button>
                  )}
                </div>
              )}

              {(group?.sections?.length ?? 0) > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#64748b', marginBottom: 6 }}>
                    {sl.S}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => setAddSectionId(null)}
                      className={`form-chip${addSectionId === null ? ' form-chip--active' : ''}`}
                    >
                      {sl.none}
                    </button>
                    {group?.sections?.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setAddSectionId(s.id)}
                        className={`form-chip${addSectionId === s.id ? ' form-chip--active' : ''}`}
                        style={addSectionId === s.id ? {
                          borderColor: catColor,
                          color: catColor,
                          backgroundColor: `${catColor}18`,
                        } : undefined}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {(() => {
                  const existingIds = new Set(members?.map((m) => m.member_id) ?? []);
                  const available = (allMembers?.data ?? [])
                    .filter((m: any) => !existingIds.has(m.id));

                  if (isSearchingMembers && available.length === 0) {
                    return (
                      <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: 20 }}>
                        Se caută...
                      </p>
                    );
                  }

                  if (available.length === 0) {
                    return (
                      <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: 20 }}>
                        {debouncedMemberSearch ? 'Niciun rezultat' : 'Toți membrii sunt deja în grup'}
                      </p>
                    );
                  }

                  return available.map((m: any) => {
                    const isSelected = selectedForAdd.has(m.id);
                    return (
                      <button
                        key={m.id}
                        onClick={() => toggleSelectForAdd(m.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                          padding: '10px 12px', background: isSelected ? '#eef2ff' : 'none',
                          border: 'none', borderBottom: '1px solid #f1f5f9',
                          cursor: 'pointer', textAlign: 'left', transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'none'; }}
                      >
                        <div style={{
                          width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: isSelected ? 'none' : '2px solid #e2e8f0',
                          background: isSelected ? '#5e81f4' : '#fff',
                          color: '#fff', fontSize: 12, transition: 'all 0.15s',
                        }}>
                          {isSelected && <IonIcon icon={checkmarkOutline} style={{ fontSize: 14 }} />}
                        </div>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%', backgroundColor: catColor,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0,
                        }}>
                          {(m.first_name?.[0] ?? '').toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#1c1d21' }}>{m.first_name} {m.last_name}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{m.cell_phone || m.email || ''}</div>
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>

              {/* Batch add footer */}
              {selectedForAdd.size > 0 && (
                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>
                    {selectedForAdd.size} selectați
                  </span>
                  <button
                    className="form-btn form-btn--primary"
                    onClick={handleBatchAdd}
                    disabled={batchAddMut.isPending}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    {batchAddMut.isPending && <span className="login-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />}
                    Adaugă {selectedForAdd.size} membri
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Section Editor ──
            Bottom-sheet-like on mobile, centered card on desktop.
            Chip area scrolls when orchestra (9 sections) wraps past
            viewport height on narrow phones. */}
        {sectionEditMember && (
          <div
            className="logout-overlay"
            onClick={() => setSectionEditMember(null)}
          >
            <div
              className="section-sheet"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="section-sheet__eyebrow">{sl.change}</div>
              <h3 className="section-sheet__title">
                {sectionEditMember.memberName}
              </h3>
              <div className="section-sheet__chips">
                <button
                  type="button"
                  className={`form-chip${sectionEditMember.currentSectionId === null ? ' form-chip--active' : ''}`}
                  onClick={() => handleSectionChange(sectionEditMember.memberId, null)}
                  disabled={updateSectionMut.isPending}
                >
                  {sl.none}
                </button>
                {group?.sections?.map((s) => {
                  const isActive = sectionEditMember.currentSectionId === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className={`form-chip${isActive ? ' form-chip--active' : ''}`}
                      onClick={() => handleSectionChange(sectionEditMember.memberId, s.id)}
                      disabled={updateSectionMut.isPending}
                      style={isActive ? {
                        borderColor: catColor,
                        color: catColor,
                        backgroundColor: `${catColor}18`,
                      } : undefined}
                    >
                      {s.name}
                    </button>
                  );
                })}
              </div>
              <div className="section-sheet__actions">
                <button
                  type="button"
                  className="form-btn"
                  onClick={() => setSectionEditMember(null)}
                >
                  Anulează
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete Section Confirm ── */}
        <ConfirmDialog
          isOpen={!!deletingSectionId}
          title={sl.delTitle}
          message={sl.delMsg}
          confirmLabel="Șterge"
          variant="danger"
          isLoading={deleteSectionMut.isPending}
          onConfirm={handleDeleteSection}
          onCancel={() => setDeletingSectionId(null)}
        />

        {/* ── Remove Member Confirm ── */}
        <ConfirmDialog
          isOpen={!!removeMemberId}
          title="Eliminare Membru"
          message="Sigur doriți să eliminați acest membru din grup?"
          confirmLabel="Elimină"
          variant="danger"
          isLoading={removeMemberMut.isPending}
          onConfirm={async () => {
            if (!removeMemberId || !id) return;
            try {
              await removeMemberMut.mutateAsync({ groupId: id, memberId: removeMemberId });
              void presentToast({ message: 'Membru eliminat din grup.', duration: 2000, color: 'success', position: 'bottom' });
            } catch {
              void presentToast({ message: 'Eroare la eliminare.', duration: 2000, color: 'danger', position: 'bottom' });
            }
            setRemoveMemberId(null);
          }}
          onCancel={() => setRemoveMemberId(null)}
        />
      </IonContent>
    </IonPage>
  );
};

export default GroupDetailPage;
